import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectContent, closeDialog } from "redux/features/dialog/dialogSlice"
import {
  selectProductById,
  selectFetchStatus,
} from "redux/features/product/productSlice"
import { fetchProduct } from "redux/features/product/productThunk"
import { Progress } from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import LoadableEntity from "components/ui/LoadableEntity"
import {
  LoaderContainer,
  ErrorContainer,
  Headline,
  Title,
  Description,
} from "hocs/features/modal/elements"
import { ProductType } from "types"

export function withModalProduct<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithModalProduct = (props: GenericType) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const content = useAppSelector((state) => selectContent(state))
    const { productId } = content

    const product = productId
      ? useAppSelector(
          (state) => selectProductById(state, productId) as ProductType,
        )
      : null

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))

    useEffect(() => {
      const handleFetchProduct = () => {
        if (!fetchStatus.isFetching && productId) {
          dispatch(fetchProduct({ product_id: productId }))
        }
      }

      handleFetchProduct()
    }, [productId])

    const handleClose = () => {
      dispatch(closeDialog())
      navigate("/")
    }

    const renderLoader = () => {
      return (
        <LoaderContainer>
          <Progress color="primary" />
        </LoaderContainer>
      )
    }

    const renderError = () => {
      return (
        <ErrorContainer>
          <Close onClick={handleClose} />
          <Headline>
            <Title variant="h3">{t("features.product.loading.title")}</Title>
            <Title variant="h5">
              {t("features.product.loading.error.title")}
            </Title>
            <Description variant="h6">
              {t("features.product.loading.error.description")}
            </Description>
          </Headline>
        </ErrorContainer>
      )
    }

    return (
      <LoadableEntity
        entity={product}
        fetchStatus={fetchStatus}
        renderLoading={renderLoader}
        renderError={renderError}
      >
        {(product) => (
          <WrappedComponent
            {...props}
            product={product}
            fetchStatus={fetchStatus}
          />
        )}
      </LoadableEntity>
    )
  }

  return WithModalProduct
}
